import React, { useEffect, useRef } from 'react';
import { Equipe } from '../../types/equipe';

export interface TeamSelectorDialogProps {
  open: boolean;
  equipes: Equipe[];
  loading: boolean;
  busca: string;
  onBuscaChange: (value: string) => void;
  onClose: () => void;
  onSelect: (equipe: Equipe) => void;
  disabledTeamId?: string | null;
}

const TeamSelectorDialog: React.FC<TeamSelectorDialogProps> = ({
  open,
  equipes,
  loading,
  busca,
  onBuscaChange,
  onClose,
  onSelect,
  disabledTeamId,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      document.body.classList.add('modal-open');
      modalRef.current?.focus();
    } else {
      document.body.classList.remove('modal-open');
    }
  }, [open]);

  if (!open) return null;

  const handleBackdrop = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  const filtered = equipes.filter(e => {
    const term = busca.toLowerCase();
    return (
      e.nome.toLowerCase().includes(term) ||
      e.atletas.some(a => (a.usuario?.nome ?? '').toLowerCase().includes(term))
    );
  });

  return (
    <>
      <div className="modal-backdrop fade show" id='select-team' />
      <div
        className="modal fade show d-block"
        role="dialog"
        tabIndex={-1}
        ref={modalRef}
        onMouseDown={handleBackdrop}
        style={{ overflow: 'auto' }}
      >
        <div
          className="modal-dialog modal-dialog-scrollable"
          role="document"
          onMouseDown={e => e.stopPropagation()}
        >
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Selecionar equipe</h5>
              <button
                type="button"
                className="btn-close"
                aria-label="Fechar"
                onClick={onClose}
              />
            </div>
            <div className="modal-body">
              <input
                type="text"
                className="form-control mb-3"
                placeholder="Buscar..."
                value={busca}
                onChange={e => onBuscaChange(e.target.value)}
              />
              {loading ? (
                <div className="text-center" aria-label="Carregando equipes">
                  <div className="spinner-border" role="status" />
                </div>
              ) : (
                <ul className="list-group">
                  {filtered.map(eq => {
                    const disabled = disabledTeamId === eq.id;
                    return (
                      <li
                        key={eq.id}
                        className={`list-group-item d-flex justify-content-between align-items-center ${disabled ? 'opacity-50' : ''}`}
                      >
                        <div className="flex-grow-1">
                          <div className="d-flex flex-wrap">
                            {eq.atletas.map(a => (
                              <div
                                key={a.id}
                                className="d-flex align-items-center me-2 mb-1"
                                style={{ fontSize: 12 }}
                              >
                                <img
                                  src={a.usuario?.imagemUrl || '/assets/images/user.png'}
                                  alt={a.usuario?.nome ?? 'Atleta'}
                                  className="rounded-circle me-1"
                                  width={24}
                                  height={24}
                                  loading="lazy"
                                  style={{ objectFit: 'cover' }}
                                />
                                <span>{a.usuario?.nome ?? 'Atleta'}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="d-flex flex-column align-items-end">
                          {disabled ? (
                            <span className="badge bg-secondary mb-2 text-black">Selecionada</span>
                          ):
                          (
                            <button
                              type="button"
                              className="btn btn-sm btn-primary"
                              aria-label={`Selecionar equipe ${eq.nome}`}
                              disabled={disabled}
                              onClick={() => onSelect(eq)}
                            >
                              Selecionar
                            </button>

                          )}
                        </div>
                      </li>
                    );
                  })}
                  {!filtered.length && (
                    <li className="list-group-item">Nenhuma equipe encontrada.</li>
                  )}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default TeamSelectorDialog;
